import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

class ProgramService {
  // Create new program
  async createProgram(coachId, programData, creatorName = 'Unknown Coach') {
    try {
      // Support both new cycle-based structure and old phase-based structure
      const isNewStructure = programData.cycles && programData.sessionsPerCycle;

      const newProgram = {
        coachId,
        creatorName, // Add creator name for public directory
        status: programData.status || 'draft', // 'draft' or 'complete'
        ...(isNewStructure ? {
          // New cycle-based structure
          name: programData.name || 'Untitled Program',
          sessionsPerCycle: programData.sessionsPerCycle || null,
          daysPerCycle: programData.daysPerCycle || null,
          numberOfCycles: programData.numberOfCycles || null,
          groupType: programData.groupType || '',
          cycles: programData.cycles || [],
          metadata: {
            description: programData.metadata?.description || '',
            category: programData.metadata?.category || 'general_fitness',
            difficulty: programData.metadata?.difficulty || 'intermediate',
            targetClient: programData.metadata?.targetClient || null,
            goals: programData.metadata?.goals || [],
            equipment: programData.metadata?.equipment || [],
            tags: programData.metadata?.tags || [],
            notes: programData.metadata?.notes || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastUsed: null,
            timesUsed: 0,
            rating: 0
          }
        } : {
          // Old phase-based structure
          metadata: {
            name: programData.name || 'Untitled Program',
            description: programData.description || '',
            category: programData.category || 'general_fitness',
            difficulty: programData.difficulty || 'intermediate',
            targetClient: programData.targetClient || null,
            duration: programData.duration || {},
            goals: programData.goals || [],
            equipment: programData.equipment || [],
            tags: programData.tags || [],
            notes: programData.notes || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastUsed: null,
            timesUsed: 0,
            rating: 0
          },
          phases: programData.phases || [],
          workouts: programData.workouts || [],
          progressionScheme: programData.progressionScheme || null,
          calendar: programData.calendar || null
        })
      };

      const docRef = await addDoc(collection(db, 'programs'), newProgram);

      return {
        id: docRef.id,
        ...newProgram,
        metadata: {
          ...newProgram.metadata,
          createdAt: new Date(),
          updatedAt: new Date()
        }
      };
    } catch (error) {
      console.error('[ProgramService] Error creating program:', error);
      throw error;
    }
  }

  // Update program
  async updateProgram(programId, updates) {
    try {
      const programRef = doc(db, 'programs', programId);

      const updateData = {
        ...updates,
        'metadata.updatedAt': serverTimestamp()
      };

      await updateDoc(programRef, updateData);

      return true;
    } catch (error) {
      console.error('[ProgramService] Error updating program:', error);
      throw error;
    }
  }

  // Delete program
  async deleteProgram(programId) {
    try {
      await deleteDoc(doc(db, 'programs', programId));
      return true;
    } catch (error) {
      console.error('[ProgramService] Error deleting program:', error);
      throw error;
    }
  }

  // Get program by ID
  async getProgram(programId) {
    try {
      const programRef = doc(db, 'programs', programId);
      const programSnap = await getDoc(programRef);

      if (programSnap.exists()) {
        return {
          id: programSnap.id,
          ...programSnap.data()
        };
      }

      return null;
    } catch (error) {
      console.error('[ProgramService] Error getting program:', error);
      throw error;
    }
  }

  // Get all programs for a coach
  async getCoachPrograms(coachId) {
    try {
      const programsRef = collection(db, 'programs');
      const q = query(
        programsRef,
        where('coachId', '==', coachId),
        orderBy('metadata.updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const programs = [];

      querySnapshot.forEach((doc) => {
        programs.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return programs;
    } catch (error) {
      console.error('[ProgramService] Error fetching programs:', error);
      return [];
    }
  }

  // Get programs for viewing: coach's drafts + all completed programs
  async getProgramsForCoach(coachId) {
    try {
      const programsRef = collection(db, 'programs');

      // Get coach's own draft programs
      const draftQuery = query(
        programsRef,
        where('coachId', '==', coachId),
        where('status', '==', 'draft')
      );

      // Get all completed programs (from any coach)
      const completeQuery = query(
        programsRef,
        where('status', '==', 'complete')
      );

      const [draftSnapshot, completeSnapshot] = await Promise.all([
        getDocs(draftQuery),
        getDocs(completeQuery)
      ]);

      const programs = [];

      draftSnapshot.forEach((doc) => {
        programs.push({
          id: doc.id,
          ...doc.data()
        });
      });

      completeSnapshot.forEach((doc) => {
        programs.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort by updated date
      programs.sort((a, b) => {
        const aDate = a.metadata?.updatedAt?.toDate?.() || new Date(0);
        const bDate = b.metadata?.updatedAt?.toDate?.() || new Date(0);
        return bDate - aDate;
      });

      return programs;
    } catch (error) {
      console.error('[ProgramService] Error fetching programs:', error);
      return [];
    }
  }

  // Get programs for a specific client
  async getClientPrograms(clientId) {
    try {
      const programsRef = collection(db, 'programs');
      const q = query(
        programsRef,
        where('metadata.targetClient.clientId', '==', clientId),
        orderBy('metadata.updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const programs = [];

      querySnapshot.forEach((doc) => {
        programs.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return programs;
    } catch (error) {
      console.error('[ProgramService] Error fetching client programs:', error);
      return [];
    }
  }

  // Duplicate program
  async duplicateProgram(programId, coachId) {
    try {
      const originalProgram = await this.getProgram(programId);

      if (!originalProgram) {
        throw new Error('Program not found');
      }

      const duplicatedProgram = {
        ...originalProgram,
        metadata: {
          ...originalProgram.metadata,
          name: `${originalProgram.metadata.name} (Copy)`,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastUsed: null,
          timesUsed: 0
        }
      };

      delete duplicatedProgram.id;

      const docRef = await addDoc(collection(db, 'programs'), duplicatedProgram);

      return {
        id: docRef.id,
        ...duplicatedProgram
      };
    } catch (error) {
      console.error('[ProgramService] Error duplicating program:', error);
      throw error;
    }
  }

  // Link program to calendar entries
  async linkProgramToCalendar(programId, calendarEntryIds) {
    try {
      const programRef = doc(db, 'programs', programId);

      await updateDoc(programRef, {
        'calendar.linkedEntries': calendarEntryIds,
        'metadata.updatedAt': serverTimestamp()
      });

      // Update each calendar entry to reference the program
      for (const entryId of calendarEntryIds) {
        const entryRef = doc(db, 'calendarEntries', entryId);
        await updateDoc(entryRef, {
          programId: programId,
          updatedAt: serverTimestamp()
        });
      }

      return true;
    } catch (error) {
      console.error('[ProgramService] Error linking program to calendar:', error);
      throw error;
    }
  }

  // Increment program usage
  async incrementProgramUsage(programId) {
    try {
      const programRef = doc(db, 'programs', programId);
      const programSnap = await getDoc(programRef);

      if (programSnap.exists()) {
        const currentTimes = programSnap.data().metadata.timesUsed || 0;

        await updateDoc(programRef, {
          'metadata.timesUsed': currentTimes + 1,
          'metadata.lastUsed': serverTimestamp()
        });
      }

      return true;
    } catch (error) {
      console.error('[ProgramService] Error incrementing usage:', error);
      throw error;
    }
  }

  // Create workout session from program
  async createWorkoutSession(programId, workoutIndex, clientId, scheduledDate) {
    try {
      const program = await this.getProgram(programId);

      if (!program || !program.workouts[workoutIndex]) {
        throw new Error('Program or workout not found');
      }

      const workout = program.workouts[workoutIndex];

      const session = {
        programId,
        programName: program.metadata.name,
        workoutIndex,
        coachId: program.coachId,
        clientId,
        scheduledDate,
        workoutData: workout,
        status: 'scheduled',
        completed: false,
        completedAt: null,
        performanceMetrics: {},
        notes: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'workoutSessions'), session);

      return {
        id: docRef.id,
        ...session
      };
    } catch (error) {
      console.error('[ProgramService] Error creating workout session:', error);
      throw error;
    }
  }

  // Search programs
  searchPrograms(programs, searchTerm, filters = {}) {
    let filtered = programs;

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(program =>
        program.metadata.name.toLowerCase().includes(term) ||
        program.metadata.description?.toLowerCase().includes(term) ||
        program.metadata.tags?.some(tag => tag.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(program => program.metadata.category === filters.category);
    }

    // Difficulty filter
    if (filters.difficulty && filters.difficulty !== 'all') {
      filtered = filtered.filter(program => program.metadata.difficulty === filters.difficulty);
    }

    // Client filter
    if (filters.clientId) {
      filtered = filtered.filter(program =>
        program.metadata.targetClient?.clientId === filters.clientId
      );
    }

    return filtered;
  }

  // Get program categories
  getProgramCategories() {
    return [
      { id: 'all', name: 'All Categories' },
      { id: 'strength', name: 'Strength' },
      { id: 'hypertrophy', name: 'Hypertrophy' },
      { id: 'endurance', name: 'Endurance' },
      { id: 'power', name: 'Power' },
      { id: 'sport_specific', name: 'Sport Specific' },
      { id: 'rehabilitation', name: 'Rehabilitation' },
      { id: 'general_fitness', name: 'General Fitness' }
    ];
  }

  // Assign program to client (creates subcollection entry)
  async assignProgramToClient(clientId, programId, assignmentData) {
    try {
      const assignedProgramRef = doc(db, 'users', clientId, 'assignedPrograms', programId);
      await setDoc(assignedProgramRef, assignmentData, { merge: true });

      console.log('[ProgramService] Program assigned to client:', clientId, programId);
      return true;
    } catch (error) {
      console.error('[ProgramService] Error assigning program to client:', error);
      throw error;
    }
  }

  // Create calendar entry for user
  async createCalendarEntry(userId, entryData) {
    try {
      const calendarRef = collection(db, 'users', userId, 'calendar');
      const entry = {
        ...entryData,
        createdAt: serverTimestamp(),
        completed: false
      };

      const docRef = await addDoc(calendarRef, entry);
      console.log('[ProgramService] Calendar entry created:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('[ProgramService] Error creating calendar entry:', error);
      throw error;
    }
  }
}

export default new ProgramService();
